<template>
  <div class="admin-page mail-analytics">
    <admin-page-header
      eyebrow="Analitik"
      title="Mail Teslimatları"
      description="SES ve rollback sağlayıcısındaki kampanya ile transactional gönderimleri, teslimat eventleriyle birlikte Türkiye saatinde (UTC+3) izleyin."
    >
      <template #actions>
        <v-btn
          outlined
          color="primary"
          small
          :loading="loading"
          @click="fetchAll"
        >
          Yenile
        </v-btn>
      </template>
    </admin-page-header>

    <v-alert
      v-if="errorMessage"
      dense
      outlined
      type="error"
      class="mb-4"
    >
      {{ errorMessage }}
    </v-alert>

    <details
      v-if="overview.coverageNote"
      class="text-caption mb-3"
    >
      <summary>Veri kapsamı ve teslimat notu</summary>
      <v-alert
        v-if="overview.coverageNote"
        dense
        text
        type="info"
        class="mb-4"
      >
        {{ overview.coverageNote }}
      </v-alert>
    </details>

    <v-card
      outlined
      class="admin-table-card mb-6"
    >
      <v-card-text>
        <div class="d-flex align-center flex-wrap mb-4">
          <span class="text-caption text--secondary mr-3 mb-1">Hızlı tarih aralığı</span>
          <v-btn-toggle
            :value="activeQuickRange"
            dense
            borderless
            class="mail-analytics__quick-ranges mb-1"
          >
            <v-btn
              v-for="range in quickRanges"
              :key="range.days"
              small
              :value="range.days"
              @click="setQuickRange(range.days)"
            >
              {{ range.label }}
            </v-btn>
          </v-btn-toggle>
          <span class="text-caption text--secondary ml-md-3 mb-1">
            Özel aralık: tek rapor isteğinde en fazla {{ formatNumber(overview.maxRangeDays || 1830) }} gün
          </span>
        </div>
        <v-row dense>
          <v-col
            cols="12"
            sm="6"
            md="2"
          >
            <v-text-field
              v-model="filters.from"
              dense
              outlined
              hide-details
              type="date"
              label="Başlangıç"
              @input="useCustomRange"
            ></v-text-field>
          </v-col>
          <v-col
            cols="12"
            sm="6"
            md="2"
          >
            <v-text-field
              v-model="filters.to"
              dense
              outlined
              hide-details
              type="date"
              label="Bitiş"
              @input="useCustomRange"
            ></v-text-field>
          </v-col>
          <v-col
            cols="12"
            sm="6"
            md="2"
          >
            <v-select
              v-model="filters.provider"
              :items="providerOptions"
              dense
              outlined
              hide-details
              label="Provider"
            ></v-select>
          </v-col>
          <v-col
            cols="12"
            sm="6"
            md="2"
          >
            <v-select
              v-model="filters.sourceType"
              :items="sourceOptions"
              dense
              outlined
              hide-details
              label="Kaynak"
            ></v-select>
          </v-col>
          <v-col
            cols="12"
            sm="6"
            md="2"
          >
            <v-select
              v-model="filters.status"
              :items="statusOptions"
              dense
              outlined
              hide-details
              label="Durum"
            ></v-select>
          </v-col>
          <v-col
            cols="12"
            sm="6"
            md="2"
          >
            <v-text-field
              v-model.trim="filters.messageKey"
              dense
              outlined
              clearable
              hide-details
              label="Şablon / mesaj"
              placeholder="welcome_v2"
              @keydown.enter.prevent="applyFilters"
            ></v-text-field>
          </v-col>
        </v-row>

        <div class="d-flex align-center justify-space-between flex-wrap mt-4">
          <div class="mail-analytics__health d-flex align-center flex-wrap">
            <v-chip
              small
              outlined
              :color="healthColor"
              class="mr-2 mb-1"
            >
              Outbox: {{ formatNumber(health.pendingOutboxCount) }} bekleyen
            </v-chip>
            <v-chip
              small
              outlined
              :color="Number(health.dueOutboxCount) > 0 ? 'error' : 'success'"
              class="mr-2 mb-1"
            >
              Due: {{ formatNumber(health.dueOutboxCount) }}
            </v-chip>
            <span class="text-caption text--secondary mb-1">
              Son provider eventi: {{ formatDateTime(health.latestProviderEventAt) }}
            </span>
            <v-chip
              v-if="comparisonPeriodLabel"
              x-small
              outlined
              color="secondary"
              class="ml-2 mb-1"
            >
              {{ comparisonPeriodLabel }}
            </v-chip>
          </div>
          <div class="d-flex align-center mt-2 mt-md-0">
            <v-btn
              text
              small
              color="primary"
              class="mr-2"
              @click="clearFilters"
            >
              Temizle
            </v-btn>
            <v-btn
              color="primary"
              small
              :loading="loading"
              @click="applyFilters"
            >
              Uygula
            </v-btn>
          </div>
        </div>

        <v-divider class="my-4"></v-divider>
        <div class="mail-analytics__operations d-flex align-center justify-space-between flex-wrap">
          <div class="d-flex align-center flex-wrap">
            <v-chip
              small
              outlined
              color="primary"
              class="mr-2 mb-2"
            >
              Provider kabulü: {{ formatNumber(summary.accepted) }}
            </v-chip>
            <v-chip
              small
              outlined
              :color="Number(summary.classificationRate) >= 99 ? 'success' : 'warning'"
              class="mr-2 mb-2"
            >
              Şablon kapsamı: %{{ formatRate(summary.classificationRate) }}
            </v-chip>
          </div>
          <div class="d-flex align-center flex-wrap">
            <v-btn
              outlined
              small
              color="primary"
              class="mr-2 mb-2"
              @click="openUnsubscribeDialog"
            >
              <v-icon
                left
                small
              >
                {{ icons.mdiEmailOffOutline }}
              </v-icon>
              Unsubscribe tercihleri · {{ formatNumber(totalUnsubscribes) }}
            </v-btn>
            <v-btn
              outlined
              small
              color="warning"
              class="mb-2"
              @click="openSuppressionDialog"
            >
              <v-icon
                left
                small
              >
                {{ icons.mdiShieldAlertOutline }}
              </v-icon>
              Suppression kayıtları · {{ formatNumber(totalSuppressions) }}
            </v-btn>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <admin-metric-strip
      :items="summaryCards"
      :loading="loading"
    />

    <div class="d-flex align-center justify-space-between flex-wrap mt-2 mb-2">
      <div>
        <div class="text-subtitle-1 font-weight-medium">
          Engagement
        </div>
        <div class="text-caption text--secondary">
          Tekil ve toplam open/click eventleri ayrı gösterilir; oranlar tracking kapsamlı teslimatlar üzerinden hesaplanır.
        </div>
      </div>
      <span class="text-caption text--secondary">
        {{ formatNumber(engagement.trackedDelivered) }} tracking kapsamlı teslimat · Son event: {{ formatDateTime(engagement.lastEventAt) }}
      </span>
    </div>

    <v-alert
      v-if="!engagement.trackingConfigured"
      dense
      outlined
      type="warning"
      class="mb-3"
    >
      Ayrı SES engagement configuration set etkin değil. Open/click değerleri kapsam başlangıcına kadar eksik veya sıfır olabilir.
    </v-alert>

    <v-row>
      <v-col
        v-for="card in engagementCards"
        :key="card.label"
        cols="12"
        sm="6"
        lg="4"
      >
        <v-card
          outlined
          class="admin-dashboard-card mail-analytics__metric"
        >
          <v-card-text>
            <div class="text-caption text--secondary">
              {{ card.label }}
            </div>
            <div class="text-h5 font-weight-bold mt-2">
              {{ card.value }}
            </div>
            <div class="text-caption text--secondary mt-2">
              {{ card.helper }}
            </div>
            <div class="text-caption mt-1">
              {{ card.comparison }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog
      v-model="unsubscribeDialog"
      max-width="1280"
      scrollable
    >
      <v-card
        outlined
        class="admin-table-card"
      >
        <v-toolbar
          dense
          flat
          class="admin-page-toolbar"
        >
          <v-toolbar-title>Unsubscribe tercihleri</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-select
            v-model="unsubscribePageSize"
            :items="pageSizeOptions"
            dense
            outlined
            hide-details
            class="mail-analytics__page-size mr-2"
            label="Sayfa"
          ></v-select>
          <v-btn
            icon
            aria-label="Unsubscribe tercihlerini kapat"
            @click="unsubscribeDialog = false"
          >
            <v-icon>{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pb-2">
          <v-row dense>
            <v-col
              cols="12"
              md="8"
            >
              <v-text-field
                v-model.trim="unsubscribeSearch"
                dense
                outlined
                clearable
                hide-details
                label="Kullanıcı ID veya tam e-posta"
                @keydown.enter.prevent="applyUnsubscribeFilters"
                @click:clear="applyUnsubscribeFilters"
              ></v-text-field>
            </v-col>
            <v-col
              cols="12"
              md="4"
            >
              <v-select
                v-model="unsubscribeScope"
                :items="unsubscribeScopeOptions"
                dense
                outlined
                hide-details
                label="Kapalı mail grubu"
                @change="applyUnsubscribeFilters"
              ></v-select>
            </v-col>
          </v-row>
          <div class="text-caption text--secondary mt-2">
            Güncel kullanıcı tercihlerini gösterir; tarih ve provider filtresinden bağımsızdır.
          </div>
        </v-card-text>

        <v-data-table
          :headers="unsubscribeHeaders"
          :items="unsubscribes"
          :loading="loadingUnsubscribes"
          :items-per-page="unsubscribePageSize"
          hide-default-footer
          class="admin-data-table elevation-0"
        >
          <template v-slot:item.user="{ item }">
            <div class="font-weight-medium mail-analytics__email">
              {{ item.email || '-' }}
            </div>
            <div class="text-caption text--secondary">
              User #{{ item.userId }}
            </div>
          </template>
          <template v-slot:item.fullUnsubscribe="{ item }">
            <v-chip
              x-small
              label
              outlined
              :color="item.fullUnsubscribe ? 'error' : 'success'"
            >
              {{ item.fullUnsubscribe ? 'Tümü kapalı' : 'Kısmi tercih' }}
            </v-chip>
          </template>
          <template v-slot:item.brandCampaignEnabled="{ item }">
            <v-chip
              x-small
              label
              outlined
              :color="preferenceColor(item.brandCampaignEnabled)"
            >
              {{ preferenceLabel(item.brandCampaignEnabled) }}
            </v-chip>
          </template>
          <template v-slot:item.collectionOpportunityEnabled="{ item }">
            <v-chip
              x-small
              label
              outlined
              :color="preferenceColor(item.collectionOpportunityEnabled)"
            >
              {{ preferenceLabel(item.collectionOpportunityEnabled) }}
            </v-chip>
          </template>
          <template v-slot:item.earningRateEnabled="{ item }">
            <v-chip
              x-small
              label
              outlined
              :color="preferenceColor(item.earningRateEnabled)"
            >
              {{ preferenceLabel(item.earningRateEnabled) }}
            </v-chip>
          </template>
          <template v-slot:item.active="{ item }">
            <v-chip
              x-small
              label
              outlined
              :color="item.active ? 'success' : 'grey'"
            >
              {{ item.active ? 'Aktif' : 'Pasif' }}
            </v-chip>
          </template>
        </v-data-table>

        <v-divider></v-divider>
        <div class="d-flex align-center justify-space-between flex-wrap pa-4">
          <span class="text-caption text--secondary">
            {{ formatNumber(totalUnsubscribes) }} kayıt · Sayfa {{ unsubscribePage + 1 }} / {{ Math.max(totalUnsubscribePages, 1) }}
          </span>
          <div>
            <v-btn
              outlined
              small
              color="primary"
              class="mr-2"
              :disabled="unsubscribePage <= 0 || loadingUnsubscribes"
              @click="goToUnsubscribePage(unsubscribePage - 1)"
            >
              Önceki
            </v-btn>
            <v-btn
              outlined
              small
              color="primary"
              :disabled="unsubscribePage + 1 >= totalUnsubscribePages || loadingUnsubscribes"
              @click="goToUnsubscribePage(unsubscribePage + 1)"
            >
              Sonraki
            </v-btn>
          </div>
        </div>
      </v-card>
    </v-dialog>

    <v-card
      outlined
      class="admin-table-card mt-6"
    >
      <v-toolbar
        dense
        flat
        class="admin-page-toolbar"
      >
        <v-toolbar-title>Teslimat gecikmesi</v-toolbar-title>
        <v-spacer></v-spacer>
        <span class="text-caption text--secondary">
          {{ formatNumber(deliveryLatency.percentileSampleCount) }} örnek / {{ formatNumber(deliveryLatency.populationCount) }} teslimat
        </span>
      </v-toolbar>
      <v-card-text>
        <v-row dense>
          <v-col
            v-for="metric in latencyMetrics"
            :key="metric.label"
            cols="6"
            md="3"
          >
            <div class="mail-analytics__latency-metric">
              <div class="text-caption text--secondary">
                {{ metric.label }}
              </div>
              <div class="text-h6 font-weight-bold mt-1">
                {{ metric.value }}
              </div>
              <div class="text-caption text--secondary">
                {{ metric.helper }}
              </div>
            </div>
          </v-col>
        </v-row>
        <div class="text-caption text--secondary mt-3">
          {{ latencyRangeLabel }}
          Büyük veri setlerinde percentile hesabı en fazla 200.000 deterministik örnek kullanır; ortalama ve sınırlar tüm popülasyondandır.
        </div>
      </v-card-text>
    </v-card>

    <v-row class="mt-2">
      <v-col
        cols="12"
        md="6"
        lg="4"
      >
        <v-card
          outlined
          class="admin-table-card fill-height"
        >
          <v-toolbar
            dense
            flat
            class="admin-page-toolbar"
          >
            <v-toolbar-title>Alıcı domainleri</v-toolbar-title>
          </v-toolbar>
          <v-simple-table dense>
            <thead>
              <tr>
                <th>Domain</th>
                <th class="text-right">
                  Gönderim
                </th>
                <th class="text-right">
                  Teslimat
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in recipientDomains"
                :key="row.key"
              >
                <td>{{ row.key || 'unknown' }}</td>
                <td class="text-right">
                  {{ formatNumber(row.prepared) }}
                </td>
                <td class="text-right">
                  %{{ formatRate(row.deliveryRate) }}
                </td>
              </tr>
              <tr v-if="!recipientDomains.length">
                <td
                  colspan="3"
                  class="text-center text--secondary py-6"
                >
                  Veri yok
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card>
      </v-col>

      <v-col
        cols="12"
        md="6"
        lg="4"
      >
        <v-card
          outlined
          class="admin-table-card fill-height"
        >
          <v-toolbar
            dense
            flat
            class="admin-page-toolbar"
          >
            <v-toolbar-title>ISP / mailbox provider</v-toolbar-title>
          </v-toolbar>
          <v-card-subtitle class="pb-0">
            Güncel alıcı domaininden tahmin edilir; MX/ISP doğrulaması değildir.
          </v-card-subtitle>
          <v-simple-table dense>
            <thead>
              <tr>
                <th>Provider</th>
                <th class="text-right">
                  Gönderim
                </th>
                <th class="text-right">
                  Teslimat
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in mailboxProviders"
                :key="row.key"
              >
                <td>{{ row.key || 'Unknown' }}</td>
                <td class="text-right">
                  {{ formatNumber(row.prepared) }}
                </td>
                <td class="text-right">
                  %{{ formatRate(row.deliveryRate) }}
                </td>
              </tr>
              <tr v-if="!mailboxProviders.length">
                <td
                  colspan="3"
                  class="text-center text--secondary py-6"
                >
                  Veri yok
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card>
      </v-col>

      <v-col
        cols="12"
        md="12"
        lg="4"
      >
        <v-card
          outlined
          class="admin-table-card fill-height"
        >
          <v-toolbar
            dense
            flat
            class="admin-page-toolbar"
          >
            <v-toolbar-title>Bounce nedenleri</v-toolbar-title>
          </v-toolbar>
          <v-simple-table dense>
            <thead>
              <tr>
                <th>Tür / neden</th>
                <th class="text-right">
                  Tekil
                </th>
                <th class="text-right">
                  Pay
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in bounceReasons"
                :key="`${row.eventType}-${row.reason}`"
              >
                <td>
                  <div>{{ bounceEventLabel(row.eventType) }}</div>
                  <div class="text-caption text--secondary">
                    {{ row.reason || 'unspecified' }}
                  </div>
                </td>
                <td class="text-right">
                  {{ formatNumber(row.uniqueDeliveries) }}
                </td>
                <td class="text-right">
                  %{{ formatRate(row.share) }}
                </td>
              </tr>
              <tr v-if="!bounceReasons.length">
                <td
                  colspan="3"
                  class="text-center text--secondary py-6"
                >
                  Veri yok
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col
        cols="12"
        lg="8"
      >
        <v-card
          outlined
          class="admin-table-card fill-height"
        >
          <v-toolbar
            dense
            flat
            class="admin-page-toolbar"
          >
            <v-toolbar-title>Gönderim ve teslimat trendi</v-toolbar-title>
            <v-spacer></v-spacer>
            <span class="text-caption text--secondary">{{ overview.timezone || 'Europe/Istanbul' }}</span>
          </v-toolbar>
          <v-card-text>
            <vue-apex-charts
              v-if="timeline.length"
              type="area"
              height="300"
              :options="chartOptions"
              :series="chartSeries"
            ></vue-apex-charts>
            <div
              v-else
              class="mail-analytics__empty text--secondary"
            >
              Seçili aralıkta gösterilecek teslimat yok.
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col
        cols="12"
        lg="4"
      >
        <v-card
          outlined
          class="admin-table-card fill-height"
        >
          <v-toolbar
            dense
            flat
            class="admin-page-toolbar"
          >
            <v-toolbar-title>Gönderimler ve şablonlar</v-toolbar-title>
          </v-toolbar>
          <v-simple-table
            dense
            class="mail-analytics__breakdown"
          >
            <thead>
              <tr>
                <th>Gönderim</th>
                <th class="text-right">
                  Hazırlanan
                </th>
                <th class="text-right">
                  Teslimat
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in messageBreakdown"
                :key="`${row.key}-${row.sourceType || 'all'}-${row.sourceRef || 'aggregate'}`"
                class="mail-analytics__template-row"
                tabindex="0"
                role="button"
                @click="openTemplateDeliveries(row)"
                @keydown.enter.prevent="openTemplateDeliveries(row)"
              >
                <td>
                  <div class="font-weight-medium mail-analytics__subject">
                    {{ templateTitle(row) }}
                  </div>
                  <div
                    v-if="row.campaignName && row.campaignName !== templateTitle(row)"
                    class="text-caption text--secondary"
                  >
                    {{ row.campaignName }}
                  </div>
                  <span class="mail-analytics__key text--secondary">
                    {{ sourceLabel(row.sourceType) }} · {{ row.name || messageKeyLabel(row.key) }}
                  </span>
                  <div class="text-caption text--secondary">
                    {{ formatDateTime(row.lastPreparedAt) }}
                  </div>
                </td>
                <td class="text-right">
                  {{ formatNumber(row.prepared) }}
                </td>
                <td class="text-right">
                  <div>%{{ formatRate(row.deliveryRate) }}</div>
                  <div class="text-caption text--secondary">
                    {{ formatNumber(row.delivered) }} teslim · {{ formatNumber(row.failed) }} hata
                  </div>
                </td>
                <td class="text-right">
                  <v-icon small>
                    {{ icons.mdiChevronRight }}
                  </v-icon>
                </td>
              </tr>
              <tr v-if="!messageBreakdown.length">
                <td
                  colspan="4"
                  class="text-center text--secondary py-8"
                >
                  Veri yok
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col
        cols="12"
        md="6"
      >
        <v-card
          outlined
          class="admin-table-card"
        >
          <v-toolbar
            dense
            flat
            class="admin-page-toolbar"
          >
            <v-toolbar-title>Kaynak dağılımı</v-toolbar-title>
          </v-toolbar>
          <v-simple-table dense>
            <thead>
              <tr>
                <th>Kaynak</th>
                <th class="text-right">
                  Hazırlandı
                </th>
                <th class="text-right">
                  Kabul
                </th>
                <th class="text-right">
                  Teslim
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in sourceBreakdown"
                :key="row.key"
              >
                <td>{{ sourceLabel(row.key) }}</td>
                <td class="text-right">
                  {{ formatNumber(row.prepared) }}
                </td>
                <td class="text-right">
                  {{ formatNumber(row.accepted) }}
                </td>
                <td class="text-right">
                  {{ formatNumber(row.delivered) }}
                </td>
              </tr>
              <tr v-if="!sourceBreakdown.length">
                <td
                  colspan="4"
                  class="text-center text--secondary py-6"
                >
                  Veri yok
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card>
      </v-col>
      <v-col
        cols="12"
        md="6"
      >
        <v-card
          outlined
          class="admin-table-card"
        >
          <v-toolbar
            dense
            flat
            class="admin-page-toolbar"
          >
            <v-toolbar-title>Durum dağılımı</v-toolbar-title>
          </v-toolbar>
          <v-simple-table dense>
            <thead>
              <tr>
                <th>Durum</th>
                <th class="text-right">
                  Kayıt
                </th>
                <th class="text-right">
                  Pay
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in statusBreakdown"
                :key="row.key"
              >
                <td>
                  <v-chip
                    x-small
                    label
                    :color="statusColor(row.key)"
                    outlined
                  >
                    {{ statusLabel(row.key) }}
                  </v-chip>
                </td>
                <td class="text-right">
                  {{ formatNumber(row.prepared) }}
                </td>
                <td class="text-right">
                  %{{ breakdownShare(row.prepared) }}
                </td>
              </tr>
              <tr v-if="!statusBreakdown.length">
                <td
                  colspan="3"
                  class="text-center text--secondary py-6"
                >
                  Veri yok
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card>
      </v-col>
    </v-row>

    <v-card
      outlined
      class="admin-table-card mt-6"
    >
      <v-toolbar
        dense
        flat
        class="admin-page-toolbar"
      >
        <v-toolbar-title>Teslimat ledger'ı</v-toolbar-title>
        <v-spacer></v-spacer>
        <span class="text-caption text--secondary mr-3">Türkiye saati (UTC+3)</span>
        <v-select
          v-model="pageSize"
          :items="pageSizeOptions"
          dense
          outlined
          hide-details
          class="mail-analytics__page-size"
          label="Sayfa"
        ></v-select>
      </v-toolbar>

      <v-card-text class="pb-2">
        <v-row dense>
          <v-col
            cols="12"
            md="6"
          >
            <v-text-field
              v-model.trim="search"
              dense
              outlined
              clearable
              hide-details
              label="ID, kullanıcı, e-posta, kampanya"
              @keydown.enter.prevent="applySearch"
              @click:clear="applySearch"
            ></v-text-field>
          </v-col>
          <v-col
            cols="12"
            sm="6"
            md="3"
          >
            <v-select
              v-model="ledgerStatus"
              :items="statusOptions"
              dense
              outlined
              hide-details
              label="Ledger durumu"
              @change="applyLedgerFilters"
            ></v-select>
          </v-col>
          <v-col
            cols="12"
            sm="6"
            md="3"
          >
            <v-select
              v-model="ledgerBounceType"
              :items="bounceTypeOptions"
              dense
              outlined
              hide-details
              label="Bounce tipi"
              @change="applyLedgerFilters"
            ></v-select>
          </v-col>
        </v-row>
      </v-card-text>

      <v-data-table
        :headers="deliveryHeaders"
        :items="deliveries"
        :loading="loadingDeliveries"
        :items-per-page="pageSize"
        hide-default-footer
        class="admin-data-table elevation-0"
      >
        <template v-slot:item.id="{ item }">
          <v-btn
            text
            x-small
            color="primary"
            @click="openEvents(item)"
          >
            #{{ item.id }}
          </v-btn>
        </template>
        <template v-slot:item.recipient="{ item }">
          <div class="font-weight-medium mail-analytics__email">
            {{ item.email || item.maskedEmail || '-' }}
          </div>
          <div class="text-caption text--secondary">
            User #{{ item.userId || '-' }}
          </div>
        </template>
        <template v-slot:item.message="{ item }">
          <div class="font-weight-medium">
            {{ item.messageName || messageKeyLabel(item.messageKey) }}
          </div>
          <div class="mail-analytics__key text--secondary">
            {{ item.messageKey || '-' }}
          </div>
          <div class="text-caption text--secondary mt-1">
            {{ sourceLabel(item.sourceType) }}
          </div>
          <div
            v-if="item.campaignName"
            class="text-caption text--secondary"
          >
            {{ item.campaignName }}
          </div>
        </template>
        <template v-slot:item.provider="{ item }">
          <v-chip
            x-small
            label
            outlined
            color="primary"
          >
            {{ item.provider || '-' }}
          </v-chip>
        </template>
        <template v-slot:item.status="{ item }">
          <v-chip
            x-small
            label
            outlined
            :color="statusColor(item.status)"
          >
            {{ statusLabel(item.status) }}
          </v-chip>
          <div
            v-if="item.errorCode"
            class="text-caption error--text mt-1"
          >
            {{ item.errorCode }}
          </div>
        </template>
        <template v-slot:item.timestamps="{ item }">
          <div>{{ formatDateTime(item.createdAt) }}</div>
          <div
            v-if="item.deliveredAt"
            class="text-caption success--text"
          >
            Teslim: {{ formatDateTime(item.deliveredAt) }}
          </div>
        </template>
        <template v-slot:item.providerMessageId="{ item }">
          <span class="mail-analytics__provider-id">{{ item.providerMessageId || '-' }}</span>
        </template>
      </v-data-table>

      <v-divider></v-divider>
      <div class="d-flex align-center justify-space-between flex-wrap pa-4">
        <span class="text-caption text--secondary">
          {{ formatNumber(totalDeliveries) }} kayıt · Sayfa {{ page + 1 }} / {{ Math.max(totalPages, 1) }}
        </span>
        <div>
          <v-btn
            outlined
            small
            color="primary"
            class="mr-2"
            :disabled="page <= 0 || loadingDeliveries"
            @click="goToPage(page - 1)"
          >
            Önceki
          </v-btn>
          <v-btn
            outlined
            small
            color="primary"
            :disabled="page + 1 >= totalPages || loadingDeliveries"
            @click="goToPage(page + 1)"
          >
            Sonraki
          </v-btn>
        </div>
      </div>
    </v-card>

    <v-dialog
      v-model="suppressionDialog"
      max-width="1280"
      scrollable
    >
      <v-card
        outlined
        class="admin-table-card"
      >
        <v-toolbar
          dense
          flat
          class="admin-page-toolbar"
        >
          <v-toolbar-title>Suppression kayıtları</v-toolbar-title>
          <v-spacer></v-spacer>
          <span class="text-caption text--secondary mr-3">Türkiye saati (UTC+3)</span>
          <v-select
            v-model="suppressionPageSize"
            :items="pageSizeOptions"
            dense
            outlined
            hide-details
            class="mail-analytics__page-size mr-2"
            label="Sayfa"
          ></v-select>
          <v-btn
            icon
            aria-label="Suppression kayıtlarını kapat"
            @click="suppressionDialog = false"
          >
            <v-icon>{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pb-2">
          <v-row dense>
            <v-col
              cols="12"
              md="8"
            >
              <v-text-field
                v-model.trim="suppressionSearch"
                dense
                outlined
                clearable
                hide-details
                label="ID, alıcı, hash, kullanıcı veya provider event"
                @keydown.enter.prevent="applySuppressionFilters"
                @click:clear="applySuppressionFilters"
              ></v-text-field>
            </v-col>
            <v-col
              cols="12"
              md="4"
            >
              <v-select
                v-model="suppressionReason"
                :items="suppressionReasonOptions"
                dense
                outlined
                hide-details
                label="Suppression nedeni"
                @change="applySuppressionFilters"
              ></v-select>
            </v-col>
          </v-row>
          <div class="text-caption text--secondary mt-2">
            Event ile delivery'ye bağlanan kayıtlarda tam alıcı gösterilir. Eski içe aktarılan kayıtlarda ham adres tutulmadığı için yalnız hash bulunabilir.
          </div>
        </v-card-text>

        <v-data-table
          :headers="suppressionHeaders"
          :items="suppressions"
          :loading="loadingSuppressions"
          :items-per-page="suppressionPageSize"
          hide-default-footer
          class="admin-data-table elevation-0"
        >
          <template v-slot:item.id="{ item }">
            #{{ item.id }}
          </template>
          <template v-slot:item.recipient="{ item }">
            <div
              v-if="item.email"
              class="font-weight-medium mail-analytics__email"
            >
              {{ item.email }}
            </div>
            <div
              v-else
              class="mail-analytics__hash text-caption"
            >
              {{ item.recipientHash || '-' }}
            </div>
            <div class="text-caption text--secondary">
              User #{{ item.userId || '-' }}
            </div>
          </template>
          <template v-slot:item.reason="{ item }">
            <v-chip
              x-small
              label
              outlined
              :color="suppressionReasonColor(item.reason)"
            >
              {{ suppressionReasonLabel(item.reason) }}
            </v-chip>
          </template>
          <template v-slot:item.provider="{ item }">
            <v-chip
              x-small
              label
              outlined
              color="primary"
            >
              {{ item.provider || '-' }}
            </v-chip>
          </template>
          <template v-slot:item.sourceDeliveryId="{ item }">
            {{ item.sourceDeliveryId ? `#${item.sourceDeliveryId}` : '-' }}
          </template>
          <template v-slot:item.suppressedAt="{ item }">
            {{ formatDateTime(item.suppressedAt) }}
          </template>
          <template v-slot:item.providerEventId="{ item }">
            <span class="mail-analytics__provider-id">{{ item.providerEventId || '-' }}</span>
          </template>
        </v-data-table>

        <v-divider></v-divider>
        <div class="d-flex align-center justify-space-between flex-wrap pa-4">
          <span class="text-caption text--secondary">
            {{ formatNumber(totalSuppressions) }} kayıt · Sayfa {{ suppressionPage + 1 }} / {{ Math.max(totalSuppressionPages, 1) }}
          </span>
          <div>
            <v-btn
              outlined
              small
              color="primary"
              class="mr-2"
              :disabled="suppressionPage <= 0 || loadingSuppressions"
              @click="goToSuppressionPage(suppressionPage - 1)"
            >
              Önceki
            </v-btn>
            <v-btn
              outlined
              small
              color="primary"
              :disabled="suppressionPage + 1 >= totalSuppressionPages || loadingSuppressions"
              @click="goToSuppressionPage(suppressionPage + 1)"
            >
              Sonraki
            </v-btn>
          </div>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="templateDeliveryDialog"
      max-width="1400"
      content-class="mail-template-delivery-dialog-shell"
    >
      <v-card class="mail-template-delivery-dialog">
        <div class="mail-template-delivery-dialog__header">
          <div class="mail-template-delivery-dialog__identity">
            <div class="text-h6 font-weight-medium">
              {{ selectedTemplate ? templateTitle(selectedTemplate) : 'Gönderim alıcıları' }}
            </div>
            <div
              v-if="selectedTemplate"
              class="text-caption text--secondary"
            >
              {{ sourceLabel(selectedTemplate.sourceType) }} · {{ selectedTemplate.name || messageKeyLabel(selectedTemplate.key) }} · {{ formatDateTime(selectedTemplate.lastPreparedAt) }}
            </div>
            <div
              v-if="selectedTemplate"
              class="mail-template-delivery-dialog__summary"
            >
              <span>Hazırlanan <strong>{{ formatNumber(selectedTemplate.prepared) }}</strong></span>
              <span>Kabul <strong>{{ formatNumber(selectedTemplate.accepted) }}</strong></span>
              <span>Teslim <strong>{{ formatNumber(selectedTemplate.delivered) }}</strong></span>
              <span>Hata <strong>{{ formatNumber(selectedTemplate.failed) }}</strong></span>
            </div>
          </div>
          <div class="mail-template-delivery-dialog__actions">
            <span class="text-caption text--secondary">Türkiye saati (UTC+3)</span>
            <v-select
              v-model="templateDeliveryPageSize"
              :items="pageSizeOptions"
              dense
              outlined
              hide-details
              class="mail-analytics__page-size"
              label="Sayfa boyutu"
            ></v-select>
            <v-btn
              icon
              aria-label="Gönderim alıcılarını kapat"
              @click="templateDeliveryDialog = false"
            >
              <v-icon>{{ icons.mdiClose }}</v-icon>
            </v-btn>
          </div>
        </div>
        <v-divider></v-divider>

        <div class="mail-template-delivery-dialog__body">
          <v-card-text class="mail-template-delivery-dialog__filters">
            <v-row dense>
              <v-col
                cols="12"
                md="6"
              >
                <v-text-field
                  v-model.trim="templateDeliverySearch"
                  dense
                  outlined
                  clearable
                  hide-details
                  label="ID, kullanıcı veya tam e-posta"
                  @keydown.enter.prevent="applyTemplateDeliveryFilters"
                  @click:clear="applyTemplateDeliveryFilters"
                ></v-text-field>
              </v-col>
              <v-col
                cols="12"
                sm="6"
                md="3"
              >
                <v-select
                  v-model="templateDeliveryStatus"
                  :items="statusOptions"
                  dense
                  outlined
                  hide-details
                  label="Teslimat durumu"
                  @change="applyTemplateDeliveryFilters"
                ></v-select>
              </v-col>
              <v-col
                cols="12"
                sm="6"
                md="3"
              >
                <v-select
                  v-model="templateDeliveryBounceType"
                  :items="bounceTypeOptions"
                  dense
                  outlined
                  hide-details
                  label="Bounce tipi"
                  @change="applyTemplateDeliveryFilters"
                ></v-select>
              </v-col>
            </v-row>
          </v-card-text>

          <v-data-table
            :headers="templateDeliveryHeaders"
            :items="templateDeliveries"
            :loading="loadingTemplateDeliveries"
            :items-per-page="templateDeliveryPageSize"
            hide-default-footer
            class="admin-data-table elevation-0"
          >
            <template v-slot:item.id="{ item }">
              <v-btn
                text
                x-small
                color="primary"
                @click="openEvents(item)"
              >
                #{{ item.id }}
              </v-btn>
            </template>
            <template v-slot:item.recipient="{ item }">
              <div class="font-weight-medium mail-analytics__email">
                {{ item.email || item.maskedEmail || '-' }}
              </div>
              <div class="text-caption text--secondary">
                User #{{ item.userId || '-' }}
              </div>
            </template>
            <template v-slot:item.message="{ item }">
              <div class="font-weight-medium">
                {{ item.campaignName || item.messageName || messageKeyLabel(item.messageKey) }}
              </div>
              <div class="mail-analytics__key text--secondary">
                {{ item.messageKey || '-' }}
              </div>
            </template>
            <template v-slot:item.status="{ item }">
              <v-chip
                x-small
                label
                outlined
                :color="statusColor(item.status)"
              >
                {{ statusLabel(item.status) }}
              </v-chip>
              <div
                v-if="item.errorCode"
                class="text-caption error--text mt-1"
              >
                {{ item.errorCode }}
              </div>
            </template>
            <template v-slot:item.timestamps="{ item }">
              <div>{{ formatDateTime(item.createdAt) }}</div>
              <div
                v-if="item.deliveredAt"
                class="text-caption success--text"
              >
                Teslim: {{ formatDateTime(item.deliveredAt) }}
              </div>
            </template>
          </v-data-table>
        </div>

        <v-divider></v-divider>
        <div class="mail-template-delivery-dialog__footer">
          <span class="text-caption text--secondary">
            {{ formatNumber(totalTemplateDeliveries) }} alıcı · Sayfa {{ templateDeliveryPage + 1 }} / {{ Math.max(totalTemplateDeliveryPages, 1) }}
          </span>
          <div>
            <v-btn
              outlined
              small
              color="primary"
              class="mr-2"
              :disabled="templateDeliveryPage <= 0 || loadingTemplateDeliveries"
              @click="goToTemplateDeliveryPage(templateDeliveryPage - 1)"
            >
              Önceki
            </v-btn>
            <v-btn
              outlined
              small
              color="primary"
              :disabled="templateDeliveryPage + 1 >= totalTemplateDeliveryPages || loadingTemplateDeliveries"
              @click="goToTemplateDeliveryPage(templateDeliveryPage + 1)"
            >
              Sonraki
            </v-btn>
          </div>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="eventDialog"
      max-width="820"
    >
      <v-card>
        <v-card-title class="d-flex align-center">
          Teslimat #{{ selectedDelivery ? selectedDelivery.id : '-' }} eventleri
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click="eventDialog = false"
          >
            <v-icon>{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-subtitle v-if="selectedDelivery">
          {{ selectedDelivery.messageName || messageKeyLabel(selectedDelivery.messageKey) }} · {{ selectedDelivery.provider }} · {{ selectedDelivery.email || selectedDelivery.maskedEmail || '-' }}
        </v-card-subtitle>
        <v-divider></v-divider>
        <v-card-text class="pa-0">
          <v-data-table
            :headers="eventHeaders"
            :items="deliveryEvents"
            :loading="loadingEvents"
            hide-default-footer
            :items-per-page="200"
            class="elevation-0"
          >
            <template v-slot:item.status="{ item }">
              <v-chip
                x-small
                label
                outlined
                :color="statusColor(item.status)"
              >
                {{ statusLabel(item.status) }}
              </v-chip>
            </template>
            <template v-slot:item.occurredAt="{ item }">
              {{ formatDateTime(item.occurredAt) }}
            </template>
            <template v-slot:item.eventSubtype="{ item }">
              {{ item.eventSubtype || '-' }}
            </template>
            <template v-slot:item.error="{ item }">
              <div>{{ item.errorCode || '-' }}</div>
              <div
                v-if="item.errorMessage"
                class="text-caption text--secondary"
              >
                {{ item.errorMessage }}
              </div>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import VueApexCharts from 'vue-apexcharts'
import {
  mdiChevronRight,
  mdiClose,
  mdiEmailOffOutline,
  mdiShieldAlertOutline,
} from '@mdi/js'
import { getAccessToken } from '@/utils/panelSession'
import AdminPageHeader from '@/components/admin/AdminPageHeader.vue'
import AdminMetricStrip from '@/components/admin/AdminMetricStrip.vue'

const dateValue = offsetDays => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Istanbul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date())
  const value = type => Number(parts.find(part => part.type === type).value)
  const date = new Date(Date.UTC(value('year'), value('month') - 1, value('day')))
  date.setUTCDate(date.getUTCDate() + offsetDays)

  return date.toISOString().slice(0, 10)
}

const defaultFilters = () => ({
  from: dateValue(-6),
  to: dateValue(0),
  provider: '',
  sourceType: '',
  status: '',
  messageKey: '',
})

export default {
  components: {
    AdminPageHeader,
    AdminMetricStrip,
    VueApexCharts,
  },
  data: () => ({
    loading: false,
    loadingDeliveries: false,
    loadingUnsubscribes: false,
    loadingSuppressions: false,
    loadingTemplateDeliveries: false,
    loadingEvents: false,
    errorMessage: '',
    overview: {
      summary: {},
      comparison: { summary: {}, engagement: {} },
      engagement: {},
      deliveryLatency: {},
      timeline: [],
      sources: [],
      messageKeys: [],
      statuses: [],
      recipientDomains: [],
      mailboxProviders: [],
      bounceReasons: [],
      health: {},
    },
    filters: defaultFilters(),
    activeQuickRange: 7,
    quickRanges: [
      { label: 'Bugün', days: 1 },
      { label: '7 gün', days: 7 },
      { label: '30 gün', days: 30 },
      { label: '90 gün', days: 90 },
    ],
    search: '',
    page: 0,
    pageSize: 25,
    pageSizeOptions: [10, 25, 50, 100],
    deliveries: [],
    totalDeliveries: 0,
    totalPages: 0,
    ledgerStatus: '',
    ledgerBounceType: '',
    unsubscribeSearch: '',
    unsubscribeScope: 'ANY',
    unsubscribePage: 0,
    unsubscribePageSize: 25,
    unsubscribes: [],
    totalUnsubscribes: 0,
    totalUnsubscribePages: 0,
    suppressionSearch: '',
    suppressionReason: '',
    suppressionPage: 0,
    suppressionPageSize: 25,
    suppressions: [],
    totalSuppressions: 0,
    totalSuppressionPages: 0,
    unsubscribeDialog: false,
    suppressionDialog: false,
    templateDeliveryDialog: false,
    selectedTemplate: null,
    templateDeliverySearch: '',
    templateDeliveryStatus: '',
    templateDeliveryBounceType: '',
    templateDeliveryPage: 0,
    templateDeliveryPageSize: 25,
    templateDeliveries: [],
    totalTemplateDeliveries: 0,
    totalTemplateDeliveryPages: 0,
    eventDialog: false,
    selectedDelivery: null,
    deliveryEvents: [],
    providerOptions: [
      { text: 'Tümü', value: '' },
      { text: 'SES', value: 'SES' },
      { text: 'Mailgun (rollback)', value: 'MAILGUN' },
    ],
    sourceOptions: [
      { text: 'Tümü', value: '' },
      { text: 'Kampanya aktivasyonu', value: 'CAMPAIGN_ACTIVATION' },
      { text: 'Transactional', value: 'TRANSACTIONAL_MAIL' },
      { text: 'Ertelenmiş transactional', value: 'TRANSACTIONAL_MAIL_OUTBOX' },
      { text: 'Kampanya canary', value: 'SES_CAMPAIGN_CANARY' },
    ],
    statusOptions: [
      { text: 'Tümü', value: '' },
      { text: 'Teslim edildi', value: 'DELIVERED' },
      { text: 'Provider kabul etti', value: 'ACCEPTED' },
      { text: 'Bekliyor', value: 'PENDING' },
      { text: 'Gönderiliyor', value: 'IN_FLIGHT' },
      { text: 'Geçici hata', value: 'TEMPORARY_FAILURE' },
      { text: 'Retry tükendi', value: 'RETRY_EXHAUSTED' },
      { text: 'Kalıcı hata', value: 'PERMANENT_FAILURE' },
      { text: 'Belirsiz', value: 'AMBIGUOUS' },
      { text: 'Complaint', value: 'COMPLAINED' },
      { text: 'Suppressed', value: 'SUPPRESSED' },
      { text: 'Global hata', value: 'GLOBAL_FAILURE' },
      { text: 'Unsubscribe', value: 'UNSUBSCRIBED' },
      { text: 'Kayıtlı değil', value: 'UNREGISTERED' },
      { text: 'Atlandı', value: 'SKIPPED' },
    ],
    bounceTypeOptions: [
      { text: 'Tüm bounce tipleri', value: '' },
      { text: 'Soft bounce', value: 'SOFT' },
      { text: 'Hard bounce', value: 'HARD' },
    ],
    suppressionReasonOptions: [
      { text: 'Tüm suppression nedenleri', value: '' },
      { text: 'Unsubscribe', value: 'UNSUBSCRIBE' },
      { text: 'Hard bounce', value: 'HARD_BOUNCE' },
      { text: 'Complaint', value: 'COMPLAINT' },
      { text: 'Provider suppression', value: 'PROVIDER_SUPPRESSION' },
    ],
    unsubscribeScopeOptions: [
      { text: 'Herhangi bir grup kapalı', value: 'ANY' },
      { text: 'Tüm pazarlama mailleri kapalı', value: 'FULL' },
      { text: 'Yeni kampanya kapalı', value: 'BRAND_CAMPAIGN' },
      { text: 'Koleksiyon fırsatı kapalı', value: 'COLLECTION_OPPORTUNITY' },
      { text: 'Kazanç oranı kapalı', value: 'EARNING_RATE' },
    ],
    deliveryHeaders: [
      { text: 'ID', value: 'id', width: 85 },
      { text: 'Alıcı', value: 'recipient', width: 180 },
      { text: 'Mesaj', value: 'message', width: 240 },
      { text: 'Provider', value: 'provider', width: 100 },
      { text: 'Durum', value: 'status', width: 180 },
      { text: 'Zaman', value: 'timestamps', width: 210 },
      { text: 'Provider message ID', value: 'providerMessageId', sortable: false },
    ],
    templateDeliveryHeaders: [
      { text: 'ID', value: 'id', width: 80 },
      { text: 'Alıcı', value: 'recipient', width: 280 },
      { text: 'Mesaj', value: 'message', width: 280 },
      { text: 'Durum', value: 'status', width: 190 },
      { text: 'Zaman', value: 'timestamps', width: 220 },
    ],
    eventHeaders: [
      { text: 'Event', value: 'eventType', width: 150 },
      { text: 'Alt tür', value: 'eventSubtype', width: 130 },
      { text: 'Durum', value: 'status', width: 150 },
      { text: 'Zaman', value: 'occurredAt', width: 190 },
      { text: 'Hata', value: 'error' },
    ],
    suppressionHeaders: [
      { text: 'ID', value: 'id', width: 85 },
      { text: 'Alıcı', value: 'recipient', width: 260 },
      { text: 'Neden', value: 'reason', width: 170 },
      { text: 'Provider', value: 'provider', width: 100 },
      { text: 'Kaynak teslimat', value: 'sourceDeliveryId', width: 140 },
      { text: 'Tarih', value: 'suppressedAt', width: 190 },
      { text: 'Provider event ID', value: 'providerEventId', sortable: false },
    ],
    unsubscribeHeaders: [
      { text: 'Kullanıcı', value: 'user', width: 260 },
      { text: 'Genel', value: 'fullUnsubscribe', width: 140 },
      { text: 'Yeni kampanya', value: 'brandCampaignEnabled', width: 140 },
      { text: 'Koleksiyon fırsatı', value: 'collectionOpportunityEnabled', width: 160 },
      { text: 'Kazanç oranı', value: 'earningRateEnabled', width: 140 },
      { text: 'Hesap', value: 'active', width: 100 },
    ],
    icons: {
      mdiChevronRight,
      mdiClose,
      mdiEmailOffOutline,
      mdiShieldAlertOutline,
    },
  }),
  computed: {
    requestOptions() {
      return { headers: { Authorization: `Bearer ${getAccessToken()}` } }
    },
    summary() {
      return this.overview.summary || {}
    },
    comparison() {
      return this.overview.comparison || {}
    },
    previousSummary() {
      return this.comparison.summary || {}
    },
    engagement() {
      return this.overview.engagement || {}
    },
    previousEngagement() {
      return this.comparison.engagement || {}
    },
    deliveryLatency() {
      return this.overview.deliveryLatency || {}
    },
    health() {
      return this.overview.health || {}
    },
    timeline() {
      return this.overview.timeline || []
    },
    sourceBreakdown() {
      return this.overview.sources || []
    },
    messageBreakdown() {
      return this.overview.messageKeys || []
    },
    statusBreakdown() {
      return this.overview.statuses || []
    },
    recipientDomains() {
      return this.overview.recipientDomains || []
    },
    mailboxProviders() {
      return this.overview.mailboxProviders || []
    },
    bounceReasons() {
      return this.overview.bounceReasons || []
    },
    comparisonPeriodLabel() {
      if (!this.comparison.from || !this.comparison.toExclusive) return ''

      return `Önceki dönem: ${this.formatDateRange(this.comparison.from, this.comparison.toExclusive)}`
    },
    healthColor() {
      return Number(this.health.pendingOutboxCount) > 0 ? 'warning' : 'success'
    },
    summaryCards() {
      return [
        {
          label: 'Hazırlanan',
          value: this.formatNumber(this.summary.prepared),
          helper: 'Seçili aralıktaki logical delivery',
          comparison: this.comparisonCount(this.summary.prepared, this.previousSummary.prepared),
        },
        {
          label: 'Teslim edildi',
          value: this.formatNumber(this.summary.delivered),
          helper: 'Provider delivery eventi alınan',
          badge: `%${this.formatRate(this.summary.deliveryRate)}`,
          color: 'success',
          comparison: this.comparisonRate(this.summary.deliveryRate, this.previousSummary.deliveryRate),
        },
        {
          label: 'Hatalı',
          value: this.formatNumber(this.summary.failed),
          helper: 'Geçici, kalıcı, global ve belirsiz sonuçlar',
          badge: Number(this.summary.failed) > 0 ? 'İncele' : '',
          color: 'error',
          comparison: this.comparisonCount(this.summary.failed, this.previousSummary.failed),
        },
        {
          label: 'Açık durum',
          value: this.formatNumber(this.summary.activeIssues),
          helper: 'Bekleyen, retry, belirsiz veya global hata',
          badge: Number(this.summary.activeIssues) > 0 ? 'İzle' : 'Temiz',
          color: Number(this.summary.activeIssues) > 0 ? 'warning' : 'success',
          comparison: this.comparisonCount(this.summary.activeIssues, this.previousSummary.activeIssues),
        },
      ]
    },
    engagementCards() {
      return [
        {
          label: 'Open rate',
          value: `%${this.formatRate(this.engagement.openRate)}`,
          helper: `${this.formatNumber(this.engagement.uniqueOpens)} tekil / ${this.formatNumber(this.engagement.openEvents)} toplam open`,
          comparison: this.comparisonRate(this.engagement.openRate, this.previousEngagement.openRate),
        },
        {
          label: 'CTR',
          value: `%${this.formatRate(this.engagement.clickThroughRate)}`,
          helper: `${this.formatNumber(this.engagement.uniqueClicks)} tekil / ${this.formatNumber(this.engagement.clickEvents)} toplam click`,
          comparison: this.comparisonRate(this.engagement.clickThroughRate, this.previousEngagement.clickThroughRate),
        },
        {
          label: 'CTOR',
          value: `%${this.formatRate(this.engagement.clickToOpenRate)}`,
          helper: 'Tekil click / tekil open',
          comparison: this.comparisonRate(this.engagement.clickToOpenRate, this.previousEngagement.clickToOpenRate),
        },
      ]
    },
    latencyMetrics() {
      const hasData = Number(this.deliveryLatency.populationCount || 0) > 0
      const value = milliseconds => (hasData ? this.formatDuration(milliseconds) : '-')

      return [
        { label: 'P50', value: value(this.deliveryLatency.p50Ms), helper: 'Medyan teslimat süresi' },
        { label: 'P95', value: value(this.deliveryLatency.p95Ms), helper: 'Teslimatların %95 sınırı' },
        { label: 'P99', value: value(this.deliveryLatency.p99Ms), helper: 'Teslimatların %99 sınırı' },
        { label: 'Ortalama', value: value(this.deliveryLatency.averageMs), helper: 'Tüm geçerli teslimatlar' },
      ]
    },
    latencyRangeLabel() {
      if (!Number(this.deliveryLatency.populationCount || 0)) {
        return 'Seçili aralıkta gecikmesi hesaplanabilen teslimat yok.'
      }

      return `Minimum ${this.formatDuration(this.deliveryLatency.minimumMs)} · Maksimum ${this.formatDuration(this.deliveryLatency.maximumMs)}.`
    },
    chartSeries() {
      return [
        { name: 'Hazırlanan', data: this.timeline.map(item => Number(item.prepared || 0)) },
        { name: 'Kabul', data: this.timeline.map(item => Number(item.accepted || 0)) },
        { name: 'Teslim', data: this.timeline.map(item => Number(item.delivered || 0)) },
        { name: 'Hata', data: this.timeline.map(item => Number(item.failed || 0)) },
      ]
    },
    chartOptions() {
      const textColor = this.$vuetify.theme.dark ? '#f5f5f5' : '#555'

      return {
        chart: { toolbar: { show: false }, zoom: { enabled: false } },
        colors: ['#6b7280', '#2563eb', '#16a34a', '#dc2626'],
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 2 },
        fill: { type: 'gradient', gradient: { opacityFrom: 0.3, opacityTo: 0.03 } },
        legend: { position: 'top', labels: { colors: textColor } },
        xaxis: {
          categories: this.timeline.map(item => this.formatShortDate(item.date)),
          labels: { style: { colors: textColor } },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: { min: 0, forceNiceScale: true, labels: { style: { colors: textColor } } },
        grid: { strokeDashArray: 4 },
        tooltip: { shared: true },
      }
    },
  },
  watch: {
    pageSize() {
      this.page = 0
      this.fetchDeliveries().catch(() => {})
    },
    unsubscribePageSize() {
      this.unsubscribePage = 0
      this.fetchUnsubscribes().catch(() => {})
    },
    suppressionPageSize() {
      this.suppressionPage = 0
      this.fetchSuppressions().catch(() => {})
    },
    templateDeliveryPageSize() {
      if (!this.selectedTemplate) return
      this.templateDeliveryPage = 0
      this.fetchTemplateDeliveries().catch(() => {})
    },
  },
  mounted() {
    this.fetchAll()
  },
  methods: {
    analyticsUrl(path) {
      return `${process.env.VUE_APP_BASE_API_URL}admin/analytics${path}`
    },
    filterParams() {
      const params = {}
      Object.keys(this.filters).forEach(key => {
        if (this.filters[key] !== '' && this.filters[key] !== null) params[key] = this.filters[key]
      })

      return params
    },
    async fetchAll() {
      this.loading = true
      this.loadingDeliveries = true
      this.loadingUnsubscribes = true
      this.loadingSuppressions = true
      this.errorMessage = ''
      try {
        await Promise.all([
          this.fetchOverview(),
          this.fetchDeliveries(false),
          this.fetchUnsubscribes(false),
          this.fetchSuppressions(false),
        ])
      } catch (error) {
        this.errorMessage = this.errorText(error)
      } finally {
        this.loading = false
        this.loadingDeliveries = false
        this.loadingUnsubscribes = false
        this.loadingSuppressions = false
      }
    },
    async fetchOverview() {
      const response = await axios.get(this.analyticsUrl('/mail'), {
        ...this.requestOptions,
        params: this.filterParams(),
      })
      this.overview = response?.data?.data || {}
    },
    async fetchDeliveries(manageLoading = true) {
      if (manageLoading) this.loadingDeliveries = true
      try {
        const response = await axios.get(this.analyticsUrl('/mail/deliveries'), {
          ...this.requestOptions,
          params: {
            ...this.filterParams(),
            status: this.ledgerStatus || this.filters.status || undefined,
            bounceType: this.ledgerBounceType || undefined,
            search: this.search || undefined,
            page: this.page,
            size: this.pageSize,
          },
        })
        const payload = response?.data?.data || {}
        this.deliveries = payload.content || []
        this.totalDeliveries = Number(payload.totalElements || 0)
        this.totalPages = Number(payload.totalPages || 0)
      } catch (error) {
        if (manageLoading) this.errorMessage = this.errorText(error)
        throw error
      } finally {
        if (manageLoading) this.loadingDeliveries = false
      }
    },
    async fetchTemplateDeliveries() {
      if (!this.selectedTemplate) return
      this.loadingTemplateDeliveries = true
      try {
        const response = await axios.get(this.analyticsUrl('/mail/deliveries'), {
          ...this.requestOptions,
          params: {
            ...this.filterParams(),
            sourceType: this.selectedTemplate.sourceType || this.filters.sourceType || undefined,
            messageKey: this.selectedTemplate.key || this.filters.messageKey || undefined,
            sourceRef: this.selectedTemplate.sourceRef || undefined,
            status: this.templateDeliveryStatus || undefined,
            bounceType: this.templateDeliveryBounceType || undefined,
            search: this.templateDeliverySearch || undefined,
            page: this.templateDeliveryPage,
            size: this.templateDeliveryPageSize,
          },
        })
        const payload = response?.data?.data || {}
        this.templateDeliveries = payload.content || []
        this.totalTemplateDeliveries = Number(payload.totalElements || 0)
        this.totalTemplateDeliveryPages = Number(payload.totalPages || 0)
      } catch (error) {
        this.errorMessage = this.errorText(error)
        throw error
      } finally {
        this.loadingTemplateDeliveries = false
      }
    },
    async fetchSuppressions(manageLoading = true) {
      if (manageLoading) this.loadingSuppressions = true
      try {
        const response = await axios.get(this.analyticsUrl('/mail/suppressions'), {
          ...this.requestOptions,
          params: {
            from: this.filters.from || undefined,
            to: this.filters.to || undefined,
            provider: this.filters.provider || undefined,
            reason: this.suppressionReason || undefined,
            search: this.suppressionSearch || undefined,
            page: this.suppressionPage,
            size: this.suppressionPageSize,
          },
        })
        const payload = response?.data?.data || {}
        this.suppressions = payload.content || []
        this.totalSuppressions = Number(payload.totalElements || 0)
        this.totalSuppressionPages = Number(payload.totalPages || 0)
      } catch (error) {
        if (manageLoading) this.errorMessage = this.errorText(error)
        throw error
      } finally {
        if (manageLoading) this.loadingSuppressions = false
      }
    },
    async fetchUnsubscribes(manageLoading = true) {
      if (manageLoading) this.loadingUnsubscribes = true
      try {
        const response = await axios.get(this.analyticsUrl('/mail/unsubscribes'), {
          ...this.requestOptions,
          params: {
            scope: this.unsubscribeScope || undefined,
            search: this.unsubscribeSearch || undefined,
            page: this.unsubscribePage,
            size: this.unsubscribePageSize,
          },
        })
        const payload = response?.data?.data || {}
        this.unsubscribes = payload.content || []
        this.totalUnsubscribes = Number(payload.totalElements || 0)
        this.totalUnsubscribePages = Number(payload.totalPages || 0)
      } catch (error) {
        if (manageLoading) this.errorMessage = this.errorText(error)
        throw error
      } finally {
        if (manageLoading) this.loadingUnsubscribes = false
      }
    },
    setQuickRange(days) {
      this.activeQuickRange = days
      this.filters = {
        ...this.filters,
        from: dateValue(1 - days),
        to: dateValue(0),
      }
      this.applyFilters()
    },
    useCustomRange() {
      this.activeQuickRange = null
    },
    applyFilters() {
      this.page = 0
      this.unsubscribePage = 0
      this.suppressionPage = 0
      this.templateDeliveryDialog = false
      this.selectedTemplate = null
      this.fetchAll()
    },
    clearFilters() {
      this.filters = defaultFilters()
      this.activeQuickRange = 7
      this.search = ''
      this.ledgerStatus = ''
      this.ledgerBounceType = ''
      this.unsubscribeSearch = ''
      this.unsubscribeScope = 'ANY'
      this.suppressionSearch = ''
      this.suppressionReason = ''
      this.page = 0
      this.unsubscribePage = 0
      this.suppressionPage = 0
      this.templateDeliveryDialog = false
      this.selectedTemplate = null
      this.fetchAll()
    },
    applySearch() {
      this.page = 0
      this.fetchDeliveries().catch(() => {})
    },
    applyLedgerFilters() {
      this.page = 0
      this.fetchDeliveries().catch(() => {})
    },
    applyUnsubscribeFilters() {
      this.unsubscribePage = 0
      this.fetchUnsubscribes().catch(() => {})
    },
    applySuppressionFilters() {
      this.suppressionPage = 0
      this.fetchSuppressions().catch(() => {})
    },
    applyTemplateDeliveryFilters() {
      this.templateDeliveryPage = 0
      this.fetchTemplateDeliveries().catch(() => {})
    },
    goToPage(nextPage) {
      this.page = Math.max(0, nextPage)
      this.fetchDeliveries().catch(() => {})
    },
    goToSuppressionPage(nextPage) {
      this.suppressionPage = Math.max(0, nextPage)
      this.fetchSuppressions().catch(() => {})
    },
    goToUnsubscribePage(nextPage) {
      this.unsubscribePage = Math.max(0, nextPage)
      this.fetchUnsubscribes().catch(() => {})
    },
    goToTemplateDeliveryPage(nextPage) {
      this.templateDeliveryPage = Math.max(0, nextPage)
      this.fetchTemplateDeliveries().catch(() => {})
    },
    openUnsubscribeDialog() {
      this.unsubscribeDialog = true
      this.fetchUnsubscribes().catch(() => {})
    },
    openSuppressionDialog() {
      this.suppressionDialog = true
      this.fetchSuppressions().catch(() => {})
    },
    openTemplateDeliveries(template) {
      this.selectedTemplate = template
      this.templateDeliverySearch = ''
      this.templateDeliveryStatus = ''
      this.templateDeliveryBounceType = ''
      this.templateDeliveryPage = 0
      this.templateDeliveries = []
      this.totalTemplateDeliveries = 0
      this.totalTemplateDeliveryPages = 0
      this.templateDeliveryDialog = true
      this.fetchTemplateDeliveries().catch(() => {})
    },
    async openEvents(delivery) {
      this.selectedDelivery = delivery
      this.deliveryEvents = []
      this.eventDialog = true
      this.loadingEvents = true
      try {
        const response = await axios.get(
          this.analyticsUrl(`/mail/deliveries/${delivery.id}/events`),
          this.requestOptions,
        )
        this.deliveryEvents = response?.data?.data || []
      } catch (error) {
        this.errorMessage = this.errorText(error)
      } finally {
        this.loadingEvents = false
      }
    },
    errorText(error) {
      return error?.response?.data?.message || 'Mail analitiği yüklenemedi.'
    },
    formatNumber(value) {
      return new Intl.NumberFormat('tr-TR').format(Number(value || 0))
    },
    formatRate(value) {
      return Number(value || 0).toLocaleString('tr-TR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })
    },
    comparisonCount(current, previous) {
      const previousValue = Number(previous || 0)
      const difference = Number(current || 0) - previousValue
      const sign = difference > 0 ? '+' : ''

      return `Önceki: ${this.formatNumber(previousValue)} · fark ${sign}${this.formatNumber(difference)}`
    },
    comparisonRate(current, previous) {
      const previousValue = Number(previous || 0)
      const difference = Number(current || 0) - previousValue
      const sign = difference > 0 ? '+' : ''

      return `Önceki: %${this.formatRate(previousValue)} · ${sign}${this.formatRate(difference)} puan`
    },
    formatDuration(value) {
      const milliseconds = Number(value || 0)
      if (milliseconds < 1000) return `${Math.round(milliseconds)} ms`
      if (milliseconds < 60000) return `${this.formatRate(milliseconds / 1000)} sn`
      if (milliseconds < 3600000) return `${this.formatRate(milliseconds / 60000)} dk`

      return `${this.formatRate(milliseconds / 3600000)} sa`
    },
    formatDateRange(from, toExclusive) {
      const start = new Date(from)
      const end = new Date(toExclusive)
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return '-'
      end.setMilliseconds(end.getMilliseconds() - 1)
      const options = {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        timeZone: 'Europe/Istanbul',
      }

      return `${start.toLocaleDateString('tr-TR', options)} – ${end.toLocaleDateString('tr-TR', options)}`
    },
    formatDateTime(value) {
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '-'

      return date.toLocaleString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Europe/Istanbul',
      })
    },
    formatShortDate(value) {
      if (!value) return '-'
      const date = new Date(`${value}T00:00:00+03:00`)
      if (Number.isNaN(date.getTime())) return value

      return date.toLocaleDateString('tr-TR', {
        day: '2-digit', month: 'short', timeZone: 'Europe/Istanbul',
      })
    },
    sourceLabel(value) {
      const labels = {
        CAMPAIGN_ACTIVATION: 'Kampanya aktivasyonu',
        TRANSACTIONAL_MAIL: 'Transactional',
        TRANSACTIONAL_MAIL_OUTBOX: 'Ertelenmiş transactional',
        SES_CAMPAIGN_CANARY: 'Kampanya canary',
      }

      return labels[value] || value || '-'
    },
    messageKeyLabel(value) {
      const labels = {
        password_reset_v2: 'Şifre Sıfırlama',
        email_verification_v2: 'E-posta Doğrulama',
        google_account_link_verification_v2: 'Google Hesabı Bağlama Doğrulaması',
        apple_account_link_verification_v2: 'Apple Hesabı Bağlama Doğrulaması',
        social_account_link_verification_v2: 'Sosyal Hesap Bağlama Doğrulaması',
        account_deletion_verification_v2: 'Hesap Silme Doğrulaması',
        phone_verified_v2: 'Telefon Doğrulandı',
        welcome_v2: 'Hoş Geldin',
        campaign_announcement_v2_new_campaign: 'Yeni Kampanya',
        campaign_announcement_v2_new_collection: 'Yeni Koleksiyon Fırsatı',
        brand_announcement_v2: 'Marka Duyurusu',
        payout_change_v2: 'Ödeme Değişikliği',
        link_created_v2: 'Link Oluşturuldu',
        link_passive_v2: 'Link Pasife Alındı',
        link_completed_v2: 'Link Tamamlandı',
        earning_confirmed_v2: 'Kazanç Onaylandı',
        cashback_confirmed_v2: 'Cashback Onaylandı',
        withdraw_request_v2: 'Para Çekme Talebi',
        withdraw_success_v2: 'Para Çekme Başarılı',
        withdraw_rejected_v2: 'Para Çekme Reddedildi',
        bank_account_changed_v2_added: 'Banka Hesabı Eklendi',
        bank_account_changed_v2_updated: 'Banka Hesabı Güncellendi',
        bank_account_changed_v2_deleted: 'Banka Hesabı Silindi',
        invoice_updated_v2: 'Fatura Güncellendi',
        referral_reward_v2: 'Referans Ödülü',
        premium_success_v2: 'Premium Başarılı',
        premium_end_v2: 'Premium Sona Erdi',
        new_adverport_announcement_v2: 'Adverport Duyurusu',
        campaign_canary: 'Kampanya Canary',
        unclassified_transactional: 'Sınıflandırılmamış Transactional',
        unclassified: 'Sınıflandırılmamış',
      }

      return labels[value] || value || '-'
    },
    templateTitle(template) {
      if (!template) return '-'

      return template.subject || template.campaignName || template.name || this.messageKeyLabel(template.key)
    },
    suppressionReasonLabel(value) {
      const labels = {
        UNSUBSCRIBE: 'Unsubscribe',
        HARD_BOUNCE: 'Hard bounce',
        COMPLAINT: 'Complaint',
        PROVIDER_SUPPRESSION: 'Provider suppression',
      }

      return labels[value] || value || '-'
    },
    suppressionReasonColor(value) {
      if (value === 'UNSUBSCRIBE') return 'grey'
      if (value === 'HARD_BOUNCE' || value === 'COMPLAINT') return 'error'

      return 'warning'
    },
    preferenceLabel(enabled) {
      return enabled ? 'Açık' : 'Kapalı'
    },
    preferenceColor(enabled) {
      return enabled ? 'success' : 'error'
    },
    bounceEventLabel(value) {
      const labels = {
        PERMANENT_BOUNCE: 'Kalıcı bounce',
        TRANSIENT_BOUNCE: 'Geçici bounce',
      }

      return labels[value] || value || '-'
    },
    statusLabel(value) {
      const labels = {
        PENDING: 'Bekliyor',
        IN_FLIGHT: 'Gönderiliyor',
        ACCEPTED: 'Kabul edildi',
        DELIVERED: 'Teslim edildi',
        TEMPORARY_FAILURE: 'Geçici hata',
        RETRY_EXHAUSTED: 'Retry tükendi',
        PERMANENT_FAILURE: 'Kalıcı hata',
        GLOBAL_FAILURE: 'Global hata',
        AMBIGUOUS: 'Belirsiz',
        SUPPRESSED: 'Suppressed',
        COMPLAINED: 'Complaint',
        UNSUBSCRIBED: 'Unsubscribe',
        UNREGISTERED: 'Kayıtlı değil',
        SKIPPED: 'Atlandı',
      }

      return labels[value] || value || '-'
    },
    statusColor(value) {
      if (value === 'DELIVERED') return 'success'
      if (value === 'ACCEPTED') return 'primary'
      if (['PENDING', 'IN_FLIGHT', 'TEMPORARY_FAILURE'].includes(value)) return 'warning'
      if (['SUPPRESSED', 'UNSUBSCRIBED', 'UNREGISTERED', 'SKIPPED'].includes(value)) return 'grey'
      if (['PERMANENT_FAILURE', 'GLOBAL_FAILURE', 'RETRY_EXHAUSTED', 'AMBIGUOUS', 'COMPLAINED'].includes(value)) return 'error'

      return 'secondary'
    },
    breakdownShare(count) {
      const prepared = Number(this.summary.prepared || 0)
      if (!prepared) return '0'

      return this.formatRate((Number(count || 0) * 100) / prepared)
    },
  },
}
</script>

<style lang="scss" scoped>
.mail-analytics__metric {
  min-height: 134px;
}

.mail-analytics__quick-ranges {
  flex-wrap: wrap;
}

.mail-analytics__operations {
  gap: 8px;
}

.mail-analytics__template-row {
  cursor: pointer;
  transition: background-color 120ms ease;
}

.mail-analytics__template-row:hover,
.mail-analytics__template-row:focus {
  background: rgba(37, 99, 235, 0.06);
  outline: none;
}

.mail-analytics__subject {
  max-width: 360px;
  overflow-wrap: anywhere;
}

.mail-analytics__latency-metric {
  min-height: 88px;
  padding: 12px;
  border: 1px solid rgba(128, 128, 128, 0.2);
  border-radius: 8px;
}

.mail-analytics__empty {
  min-height: 270px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mail-analytics__key,
.mail-analytics__provider-id {
  display: block;
  max-width: 250px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mail-analytics__email,
.mail-analytics__hash {
  overflow-wrap: anywhere;
}

.mail-analytics__hash {
  max-width: 260px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
}

.mail-analytics__key {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.78rem;
}

.mail-analytics__provider-id {
  max-width: 300px;
  font-size: 0.75rem;
}

.mail-analytics__search {
  max-width: 300px;
}

.mail-analytics__page-size {
  width: 132px;
  max-width: 132px;
}

.mail-template-delivery-dialog {
  display: flex;
  max-height: min(90vh, 920px);
  flex-direction: column;
  overflow: hidden;
}

.mail-template-delivery-dialog__header,
.mail-template-delivery-dialog__footer {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 20px;
}

.mail-template-delivery-dialog__identity {
  min-width: 0;
}

.mail-template-delivery-dialog__identity > div {
  overflow-wrap: anywhere;
}

.mail-template-delivery-dialog__summary {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
}

.mail-template-delivery-dialog__summary span {
  padding: 4px 8px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 999px;
  color: rgba(15, 23, 42, 0.68);
  font-size: 12px;
}

.mail-template-delivery-dialog__actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 12px;
}

.mail-template-delivery-dialog__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
}

.mail-template-delivery-dialog__filters {
  padding: 16px 20px 8px;
}

.mail-template-delivery-dialog__footer {
  flex-wrap: wrap;
}

@media (max-width: 960px) {
  .mail-analytics__search {
    max-width: 220px;
  }

  .mail-template-delivery-dialog__header {
    align-items: flex-start;
  }

  .mail-template-delivery-dialog__actions {
    flex-wrap: wrap;
    justify-content: flex-end;
  }
}

@media (max-width: 600px) {
  .mail-template-delivery-dialog__header,
  .mail-template-delivery-dialog__footer {
    align-items: stretch;
    flex-direction: column;
  }

  .mail-template-delivery-dialog__actions {
    justify-content: space-between;
  }
}
</style>
